import * as React from "react"
import { PageContainer } from "./PageContainer"
import {
    Rect,
    LayerProps,
    PropertyControls,
    ControlType,
    Size,
    isFiniteNumber,
    FrameProps,
    FrameWithMotion,
    Point,
} from "../render"
import { EmptyState } from "./EmptyState"
import { FramerEvent } from "../events"
import { RenderTarget, RenderEnvironment } from "../render/types/RenderEnvironment"
import { isReactChild, isReactElement } from "../utils/type-guards"
import { Scroll, ScrollProps } from "./Scroll"
import { MotionValue, animationControls, motionValue, unwrapMotionValue } from "framer-motion"
import { isMotionValue } from "../render/utils/isMotionValue"
import { inertia } from "popmotion"
import { Omit } from "../utils/omit"
import { paddingFromProps } from "./utils/paddingFromProps"
import { defaultFrameRect, unwrapFrameProps } from "../render/presentation/Frame/FrameWithMotion"
import {
    ConstraintsContext,
    calculateRect,
    constraintsEnabled,
    ParentSizeState,
    ParentSize,
    calculateSize,
} from "../render/types/NewConstraints"

export type PageDirection = "horizontal" | "vertical"

namespace ContentDimension {
    export const Auto: PageContentDimension = "auto"
    export const Stretch: PageContentDimension = "stretch"
}
export type PageContentDimension = "auto" | "stretch"
const pageContentDimensionOptions: PageContentDimension[] = [ContentDimension.Auto, ContentDimension.Stretch]
const pageContentDimensionTitles = pageContentDimensionOptions.map(option => {
    switch (option) {
        case ContentDimension.Auto:
            return "Auto"
        case ContentDimension.Stretch:
            return "Stretch"
    }
}) as string[]

/**
 * @public
 */
export type PageAlignment = "start" | "center" | "end"

const pageAlignmentOptions: PageAlignment[] = ["start", "center", "end"]
const genericAlignmentTitles = pageAlignmentOptions.map(option => {
    switch (option) {
        case "start":
            return "Start"
        case "center":
            return "Center"
        case "end":
            return "End"
    }
})
const horizontalAlignmentTitles = pageAlignmentOptions.map(option => {
    switch (option) {
        case "start":
            return "Left"
        case "center":
            return "Center"
        case "end":
            return "Right"
    }
})
const verticalAlignmentTitles = pageAlignmentOptions.map(option => {
    switch (option) {
        case "start":
            return "Top"
        case "center":
            return "Center"
        case "end":
            return "Bottom"
    }
})

/**
 * Page effects change the behavior of the transition when swiping between pages.
 * By default there is no page effect applied.
 * @remarks
 * ```jsx
 * import * as React from "react"
 * import { Page, PageEffect } from "framer"
 *
 * export function MyComponent() {
 *  return <Page defaultEffect={"cube"} />
 * }
 * ```
 *
 * `"none"` - No custom effect is applied. This is the default.
 * `"cube"` - Each page is positioned as a 3D cube, connected to the current page.
 * `"coverflow"` - Each page is positioned in 3D, behind the current page.
 * `"wheel"` - Each page is gently titled in 3D, like a wheel.
 * `"pile"` - Each page is stacked behind the current page.
 * @public
 */
export type PageEffect = "none" | "cube" | "coverflow" | "wheel" | "pile"

const pageEffectOptions: PageEffect[] = ["none", "cube", "coverflow", "wheel", "pile"]
const pageEffectTitles = pageEffectOptions.map(option => {
    switch (option) {
        case "none":
            return "None"
        case "cube":
            return "Cube"
        case "coverflow":
            return "Cover Flow"
        case "wheel":
            return "Wheel"
        case "pile":
            return "Pile"
    }
})

/**
 * Information about the current effect.
 * @public
 */
export interface PageEffectInfo {
    /**
     * The offset of this page, in pixels, measured from the left-most part of the container.
     * @public
     */
    offset: number
    /**
     * The offset of this page, normalised to the page size.
     *
     * For instance, if each page is `200` pixels wide, and we're on page index `1`, the `normalizedOffset` of page index `0` will be `-1`.
     * @public
     */
    normalizedOffset: number
    /**
     * The `width` and `height` of the page.
     * @public
     */
    size: Size
    /**
     * The index of the current page. The first page is `0`, the second is `1` and so on.
     * @public
     */
    index: number
    /**
     * The direction of page scrolling, `"horizontal"` or `"vertical"`
     * @public
     */
    direction: PageDirection
    /**
     * The gap between each page, in pixels.
     * @public
     */
    gap: number
    /**
     * The total number of pages.
     *
     * @public
     */
    pageCount: number // Infinity when looped
}
export type CustomPageEffect = (info: PageEffectInfo) => PageEffectValues

export type PageEventHandler = (event: FramerEvent, pageComponent: Page) => void

export type PageEffectValues = { [key: string]: string | number | boolean }

/**
 * Event callbacks for the Page component, can be used to react to and co-ordinate
 * with other components.
 *
 * @public
 */
export interface PageEvents {
    /**
     * A callback that will be invoked when changing the page.
     * @remarks
     * This will be invoked when the drag animation begins or when the page changes
     * programatically. It can be used to co-ordinate with other behaviors.
     *
     * @param currentIndex - The current page number
     * @param previousIndex - The index of the previous page
     * @param pageComponent - The Page component for the current page.
     * @public
     * @remarks
     * ```jsx
     * <Page
     *     onChangePage={(current, previous, page) => {
     *         console.log(current, previous, page)
     *     }}
     * />
     * ```
     */
    onChangePage(currentIndex: number, previousIndex: number, pageComponent: Page): void
}

/**
 * All properties that can be used with the {@link Page} component it also extends all {@link ScrollProps} properties.
 * ```jsx
 * <Page
 *   direction={"horizontal"}
 *   contentWidth={"stretch"}
 *   contentHeight={"stretch"}
 *   alignment={"center"}
 *   currentPage={0}
 *   animateCurrentPageUpdate={true}
 *   gap={10}
 *   padding={0}
 *   paddingPerSide={true}
 *   paddingTop={0}
 *   paddingRight={0}
 *   paddingBottom={0}
 *   paddingLeft={0}
 *   momentum={false}
 *   dragEnabled={false}
 *   defaultEffect={PageEffect.Cube}>
 *   <Frame background="#19E" />
 *   <Frame background="#5CF" />
 *   <Frame background="#2CD" />
 * </Page>
 * ```
 * @public
 */
export interface PageProperties {
    /**
     * Current swipe direction. Either "horizontal" or "vertical". Set to `"horizontal"` by
     * default.
     *
     * @remarks
     * ```jsx
     * <Page direction="horizontal" />
     * ```
     */
    direction: PageDirection
    /**
     * Width of the pages within the component. Either "auto" or "stretch" or a numeric value. Set
     * to `"stretch"` by default.
     *
     * @remarks
     * ```jsx
     * <Page contentWidth="auto" />
     * ```
     */
    contentWidth: PageContentDimension | number
    /**
     * Height of the pages within the component. Either "auto" or "stretch" or a numeric value. Set
     * to `"stretch"` by default.
     *
     * @remarks
     * ```jsx
     * <Page contentHeight="auto" />
     * ```
     */
    contentHeight: PageContentDimension | number
    /**
     * Alignment of the pages within the component. Either "start", "center", or "end". Set to
     * `"start"` by default.
     *
     * @remarks
     * ```jsx
     * <Page alignment="center" />
     * ```
     */
    alignment: PageAlignment
    /**
     * Index of the current page. Set to `0` by default.
     *
     * @remarks
     * ```jsx
     * <Page currentPage={5} />
     * ```
     */
    currentPage: number
    /**
     * Determines whether the component should animate page changes. Set to `true` by default.
     *
     * @remarks
     * ```jsx
     * <Page animateCurrentPageUpdate={false} />
     * ```
     * @beta
     */
    animateCurrentPageUpdate: boolean
    /**
     * A number describing the gap between the page elements. Set to `10` by default.
     *
     * @remarks
     * ```jsx
     * <Page gap={0} />
     * ```
     * */
    gap: number

    /**
     * Padding to be applied to all sides. Set to `0` by default.
     * To specify different padding for each side, provide
     * individual `paddingTop`, `paddingLeft`, `paddingRight` and `paddingBottom` values.
     *
     * ```jsx
     * <Page padding={20} />
     * ```
     */
    padding: number
    /**
     * Flag to tell the Page to ignore the `padding` prop and apply values per-side.
     *
     * @remarks
     *
     * ```jsx
     * <Page paddingLeft={20}  />
     * ```
     */
    paddingPerSide?: boolean
    /**
     * Value for the top padding of the container. Set to `0` by default.
     * ```jsx
     * <Page paddingTop={20}  />
     * ```
     */
    paddingTop?: number
    /**
     * ```jsx
     * <Page paddingRight={20}  />
     * ```
     * Value for the right padding of the container. Set to `0` by default.
     */
    paddingRight?: number
    /**
     * ```jsx
     * <Page paddingBottom={20}  />
     * ```
     * Value for the bottom padding of the container. Set to `0` by default.
     */
    paddingBottom?: number
    /**
     * ```jsx
     * <Page paddingLeft={20}  />
     * ```
     * Value for the left padding of the container. Set to `0` by default.
     */
    paddingLeft?: number
    /**
     * When enabled you can flick through multiple pages at once.
     * @remarks
     *
     * ```jsx
     * <Page momentum />
     * ```
     */
    momentum: boolean

    /**
     * Pick one of the predefined effects. Either "none", "cube", "coverflow", "wheel" or "pile". Set to `"none"` by default.
     * @remarks
     *
     * ```jsx
     * <Page defaultEffect={"coverflow"} />
     * ```
     */
    defaultEffect: PageEffect

    /**
     * Allows you to provide a custom transition effect for individual pages.
     *
     * This function is once for every page, every time the scroll offset changes. It returns a new set of styles for this page.
     *
     * @param info - A {@link PageEffectInfo} object with information about the current effect.
     * @returns should return a new set of Frame properties.
     *
     * ```jsx
     * function scaleEffect() {
     *     const { normalizedOffset } = info
     *     return {
     *         scale: Math.max(0, 1 + Math.min(0, normalizedOffset * -1))
     *     }
     * }
     *
     * return <Page effect={scaleEffect} />
     * ```
     *
     * @public
     */
    effect?: (info: PageEffectInfo) => PageEffectValues
}

/**
 * The following are the props accepted by the Page component, these are all in
 * addition to the standard props accepted by all Frame components.
 * @public
 */
export interface PageProps
    extends PageProperties,
        Partial<Omit<FrameProps, "size" | "onScroll">>,
        LayerProps,
        Partial<PageEvents>,
        Partial<Omit<ScrollProps, "direction" | "contentWidth" | "contentHeight">> {}
interface PageState {
    containerSize: Size | null
}
/**
 * The Page component allows you to create horizontally or vertically swipeable areas. It can be
 * imported from the Framer Library and used in code components. Add children to create pages to
 * swipe between. These children will be stretched to the size of the page component by default,
 * but can also be set to auto to maintain their original size.
 *
 * @remarks
 * ```jsx
 * import * as React from "react"
 * import { Frame, Page } from "framer"
 * export class Page extends React.Component {
 *   render() {
 *     return (
 *       <Page>
 *         <Frame />
 *       </Page>
 *     )
 *   }
 * }
 * ```
 * @public
 */
export class Page extends React.Component<Partial<PageProps>, PageState> {
    /** @internal */
    static _isStylable = true
    static supportsConstraints = true

    /** @internal */
    static contextType = ConstraintsContext
    /** @internal */
    context!: React.ContextType<
        // typeof ConstraintsContext
        React.Context<{
            size: ParentSize
        }>
    >

    private pages: { rect: Rect; effectValues: { [key: string]: MotionValue } | undefined }[] = []

    private propsBoundedCurrentPage = 0 // Bounded version of props.currentPage (see componentWillUpdate)
    private shouldReflectPropsBoundedCurrentPage = false // Whether to reflect propsBoundedCurrentPage (see componentDidUpdate)
    private contentOffset: {
        x: MotionValue<number>
        y: MotionValue<number>
    }
    private currentContentPage = 0
    private isScrolling = true
    private scrollControls = animationControls()

    private containerRef: React.RefObject<HTMLDivElement> = React.createRef()
    state: PageState = { containerSize: null }

    private static pageProps: PageProperties &
        Partial<Omit<ScrollProps, "direction" | "contentWidth" | "contentHeight" | "scrollAnimate">> = {
        direction: "horizontal",
        contentWidth: ContentDimension.Stretch,
        contentHeight: ContentDimension.Stretch,
        alignment: "start",

        currentPage: 0,
        animateCurrentPageUpdate: true,

        gap: 10,
        padding: 0,
        momentum: false,
        dragEnabled: true,

        defaultEffect: "none",

        background: "transparent",
        overflow: "hidden",
    }

    /** @internal */
    static defaultProps: PageProps = Object.assign({}, Page.pageProps)

    /** @internal */
    static propertyControls: PropertyControls<PageProps> = {
        direction: { type: ControlType.SegmentedEnum, options: ["horizontal", "vertical"], title: "Direction" },
        directionLock: {
            type: ControlType.Boolean,
            title: "Lock",
            enabledTitle: "1 Axis",
            disabledTitle: "Off",
            defaultValue: true,
        },
        contentWidth: {
            type: ControlType.SegmentedEnum,
            options: pageContentDimensionOptions,
            optionTitles: pageContentDimensionTitles,
            title: "Width",
        },
        contentHeight: {
            type: ControlType.SegmentedEnum,
            options: pageContentDimensionOptions,
            optionTitles: pageContentDimensionTitles,
            title: "Height",
        },
        alignment: {
            type: ControlType.SegmentedEnum,
            options: pageAlignmentOptions,
            optionTitles(props) {
                if (!props) return genericAlignmentTitles
                const crossDirectionIsHorizontal = props.direction !== "horizontal"
                return crossDirectionIsHorizontal ? horizontalAlignmentTitles : verticalAlignmentTitles
            },
            title: "Align",
            hidden(props) {
                const { direction, contentWidth, contentHeight } = props
                const isHorizontalDirection = direction === "horizontal"
                const crossDimension = isHorizontalDirection ? contentHeight : contentWidth
                return crossDimension === ContentDimension.Stretch
            },
        },
        gap: {
            type: ControlType.Number,
            min: 0,
            title: "Gap",
        },
        padding: {
            type: ControlType.FusedNumber,
            toggleKey: "paddingPerSide",
            toggleTitles: ["Padding", "Padding per side"],
            valueKeys: ["paddingTop", "paddingRight", "paddingBottom", "paddingLeft"],
            valueLabels: ["T", "R", "B", "L"],
            min: 0,
            title: "Padding",
        },
        currentPage: {
            type: ControlType.Number,
            min: 0,
            title: "Current",
            displayStepper: true,
        },
        momentum: {
            type: ControlType.Boolean,
            enabledTitle: "On",
            disabledTitle: "Off",
            title: "Momentum",
        },
        dragEnabled: {
            type: ControlType.Boolean,
            enabledTitle: "On",
            disabledTitle: "Off",
            title: "Dragging",
        },
        defaultEffect: {
            type: ControlType.Enum,
            options: pageEffectOptions,
            optionTitles: pageEffectTitles,
            title: "Effect",
        },
        children: {
            type: ControlType.Array,
            title: "Content",
            propertyControl: { type: ControlType.ComponentInstance, title: "Page" },
        },
    }

    private get isHorizontalDirection(): boolean {
        return this.props.direction === "horizontal"
    }

    private get currentContentOffset(): MotionValue<number> {
        return this.isHorizontalDirection ? this.contentOffset.x : this.contentOffset.y
    }

    private boundedCurrentPage(props: PageProps): number {
        // The current page property is capped to the number of children when positive, and cycles from last when negative
        const childCount = React.Children.count(props.children)
        const currentPage = props.currentPage || 0
        return currentPage >= 0
            ? Math.min(currentPage, childCount - 1)
            : ((currentPage % childCount) + childCount) % childCount
    }

    /**
     * @internal
     */
    componentDidMount() {
        this.scrollControls.mount()
        this.setContainerSize()
    }

    /**
     * @internal
     */
    componentWillUnmount() {
        this.scrollControls.unmount()
    }

    /**
     * @internal
     */
    componentWillUpdate(nextProps: PageProps) {
        if (nextProps.direction !== this.props.direction) {
            this.contentOffset.x.set(0)
            this.contentOffset.y.set(0)
        }

        const boundedCurrentPage = this.boundedCurrentPage(nextProps || this.props)

        if (this.propsBoundedCurrentPage !== boundedCurrentPage) {
            this.propsBoundedCurrentPage = boundedCurrentPage
            this.shouldReflectPropsBoundedCurrentPage = true
        }
    }

    private setContainerSize() {
        if (this.containerRef.current) {
            const elementWidth = this.containerRef.current.offsetWidth
            const elementHeight = this.containerRef.current.offsetHeight
            const stateContainerSize = this.state.containerSize
            if (
                stateContainerSize === null ||
                (stateContainerSize &&
                    (elementWidth !== stateContainerSize.width || elementHeight !== stateContainerSize.height))
            ) {
                const containerSize = {
                    width: elementWidth,
                    height: elementHeight,
                }
                this.setState({ containerSize })
            }
        }
    }

    /**
     * @internal
     */
    componentDidUpdate() {
        this.setContainerSize()
        if (
            (!this.props.children || React.Children.count(this.props.children) === 0) &&
            !this.props.__fromCodeComponentNode
        ) {
            return
        }
        if (this.shouldReflectPropsBoundedCurrentPage) {
            this.shouldReflectPropsBoundedCurrentPage = false
            if (
                this.currentContentPage !== this.propsBoundedCurrentPage &&
                RenderTarget.current() !== RenderTarget.canvas
            ) {
                if (this.props.onChangePage) {
                    this.props.onChangePage(this.propsBoundedCurrentPage, this.currentContentPage, this)
                }
                this.updateOffsetForPage(this.propsBoundedCurrentPage, this.props.animateCurrentPageUpdate)
            } else {
                // The container size might have changed, so update the offset for the page non-animated
                this.updateOffsetForPage(this.propsBoundedCurrentPage)
                if (RenderTarget.current() === RenderTarget.canvas) {
                    this.applyEffects()
                }
            }
        }
    }

    private nearestPageIndex = (
        startPosition: number,
        endPosition: number,
        isHorizontalDirection: boolean,
        allowSkippingPages: boolean
    ): number => {
        const distanceToStart = function(rect: Rect): number {
            const rectPosition = isHorizontalDirection ? rect.x : rect.y
            return Math.abs(rectPosition + startPosition)
        }

        const distanceToEnd = function(rect: Rect): number {
            const rectPosition = isHorizontalDirection ? rect.x : rect.y
            return Math.abs(rectPosition + endPosition)
        }

        if (allowSkippingPages) {
            const closestPages = [...this.pages].sort((a, b) => distanceToEnd(a.rect) - distanceToEnd(b.rect))
            return this.pages.indexOf(closestPages[0])
        } else {
            const closestToStart = [...this.pages].sort((a, b) => distanceToStart(a.rect) - distanceToStart(b.rect))
            if (closestToStart.length === 1) return this.pages.indexOf(closestToStart[0])
            const pageA = closestToStart[0]
            const pageB = closestToStart[1]
            const closestPages = [pageA, pageB].sort((a, b) => distanceToEnd(a.rect) - distanceToEnd(b.rect))
            return this.pages.indexOf(closestPages[0])
        }
    }

    private onDragEnd = (e: MouseEvent | TouchEvent, point: { velocity: Point }) => {
        this.currentContentOffset.stop()

        if (!this.isScrolling) {
            return
        }
        this.isScrolling = false

        const { momentum, onChangePage } = this.props as PageProps
        const { isHorizontalDirection } = this

        const startPosition = this.currentContentOffset.get()
        const axis = isHorizontalDirection ? "x" : "y"
        const velocity = point.velocity[axis]
        let index = this.nearestPageIndex(startPosition, startPosition, isHorizontalDirection, momentum)

        if (velocity) {
            /**
             * TODO: This is a bit hacky. We're hijacking the inertia animation for the modifyTarget functionality. Maybe this is information we can
             * pass through the `onDragEnd` event handler if `dragMomentum` is `true`.
             */
            inertia({
                from: startPosition,
                velocity,
                modifyTarget: (endPosition: number) => {
                    index = this.nearestPageIndex(startPosition, endPosition, isHorizontalDirection, momentum)

                    if (onChangePage) {
                        onChangePage(index, this.currentContentPage, this)
                    }

                    return endPosition
                },
            })
                .start()
                .stop()
        }

        const offset = this.offsetForPage(index)

        this.currentContentPage = index
        void this.scrollControls.start({
            [axis]: offset,
            transition: {
                type: "spring",
                from: this.currentContentOffset.get(),
                velocity: velocity,
                stiffness: 500,
                damping: 50,
            },
        })
    }

    private applyEffects() {
        this.pages.forEach((page, index) => {
            const pageRect = page.rect
            const values = this.effectValues(index, pageRect)
            if (page.effectValues && values) {
                for (const key in values) {
                    if (isMotionValue(page.effectValues[key])) {
                        // Because these are the actual Animatable values passed to the Frame
                        // Updating their value will modify the Frame
                        page.effectValues[key].set(values[key])
                    }
                }
            }
        })
    }

    private contentOffsetUpdated = () => {
        this.applyEffects()
    }

    private effectValues(index: number, pageRect: Rect): PageEffectValues | null {
        const { direction, defaultEffect, effect, gap } = this.props as PageProps
        const effectFunction = effect || defaultEffects(defaultEffect)
        if (!effectFunction) {
            return null
        }
        let offset: number
        let normalizedOffset: number
        if (direction === "horizontal") {
            offset = pageRect.x + (this.contentOffset ? this.contentOffset.x.get() : 0)
            normalizedOffset = offset / pageRect.width
        } else {
            offset = pageRect.y + (this.contentOffset ? this.contentOffset.y.get() : 0)
            normalizedOffset = offset / pageRect.height
        }

        const size = { width: pageRect.width, height: pageRect.height }

        return effectFunction({
            offset,
            normalizedOffset,
            size,
            index,
            direction,
            pageCount: this.pages.length,
            gap: gap,
        })
    }

    private wrapHandler<F extends (...args: any[]) => void>(ownHandler: F, originalHandler: F | undefined): F {
        if (!originalHandler) {
            return ownHandler
        }
        const wrappedFunction: any = (...args: any[]): void => {
            ownHandler(...args)
            originalHandler(...args)
        }
        return wrappedFunction
    }

    private offsetForPage(index: number): number {
        const pageCount = this.pages.length
        const currentPage = this.pages[Math.max(0, Math.min(pageCount - 1, index))]
        if (!currentPage) {
            return 0
        }
        if (this.isHorizontalDirection) {
            return -currentPage.rect.x
        } else {
            return -currentPage.rect.y
        }
    }

    private updateOffsetForPage(page: number, animated: boolean = false) {
        const offset = this.offsetForPage(page)
        this.currentContentPage = page
        if (animated) {
            const axis = this.isHorizontalDirection ? "x" : "y"
            void this.scrollControls.start({
                [axis]: offset,
                transition: {
                    type: "spring",
                    from: this.currentContentOffset.get(),
                    velocity: this.currentContentOffset.getVelocity(),
                    stiffness: 500,
                    damping: 50,
                },
            })
        } else {
            this.currentContentOffset.set(offset)
        }
    }

    private onDragStart = () => {
        this.isScrolling = true
    }

    private onDirectionLock = (lockedDirection: "x" | "y") => {
        const { direction } = this.props
        if (
            (lockedDirection === "x" && direction !== "horizontal") ||
            (lockedDirection === "y" && direction !== "vertical")
        ) {
            this.isScrolling = false
        }
    }

    /**
     * @internal
     */
    render() {
        const {
            direction,
            directionLock,
            contentWidth,
            contentHeight,
            children,
            gap,
            dragEnabled,
            wheelEnabled = false,
            overflow,
            contentOffsetX,
            contentOffsetY,
            onChangePage,
            onDrag,
            onDragStart,
            onDragEnd,
            onScroll,
            onScrollEnd,
            onScrollStart,
            onDirectionLock,
            onUpdate,
            ...props
        } = this.props as PageProps

        if (props.__fromCodeComponentNode && !constraintsEnabled(unwrapFrameProps(props))) {
            props.width = "100%"
            props.height = "100%"
            props._constraints = { enabled: true }
        }

        const parentSize = this.context.size
        let size = calculateRect(unwrapFrameProps(this.props), parentSize) || this.state.containerSize
        let shouldMeasureSize = true
        if (!size) {
            // Shortcut: if you you pass actual numbers, we take that as the size and don't measure it
            if (isFiniteNumber(props.width) && isFiniteNumber(props.height)) {
                size = {
                    width: props.width,
                    height: props.height,
                }
                shouldMeasureSize = false
            } else {
                size = defaultFrameRect
            }
        }

        const padding = paddingFromProps(this.props)
        const scrollContainerSize = {
            width: Math.max(0, size.width - padding.left - padding.right),
            height: Math.max(0, size.height - padding.top - padding.bottom),
        }

        const isHorizontalDirection = this.isHorizontalDirection

        const pageCount = React.Children.count(children)

        this.pages = []

        let maxX = 0
        let maxY = 0

        const mainDimension = isHorizontalDirection ? contentWidth : contentHeight
        const crossDimension = isHorizontalDirection ? contentHeight : contentWidth
        const alignment = crossDimension === ContentDimension.Stretch ? ContentDimension.Stretch : this.props.alignment

        const childComponents = React.Children.map(
            children,
            (child: React.ReactElement<Partial<FrameProps>>, index) => {
                if (!isReactChild(child) || !isReactElement(child)) {
                    return child
                }
                const unwrappedProps = unwrapFrameProps(child.props)
                if (unwrappedProps.width === undefined) {
                    unwrappedProps.width = defaultFrameRect.width
                }
                if (unwrappedProps.height === undefined) {
                    unwrappedProps.height = defaultFrameRect.height
                }
                // XXX: should using child.rect() be better here?
                const childSize = calculateSize(unwrappedProps, size || ParentSizeState.Unknown)
                if (!childSize) {
                    return child
                }
                const childRect = {
                    x: maxX,
                    y: maxY,
                    ...childSize,
                }

                if (mainDimension === ContentDimension.Stretch) {
                    if (isHorizontalDirection) {
                        childRect.width = scrollContainerSize.width
                    } else {
                        childRect.height = scrollContainerSize.height
                    }
                }

                if (isFiniteNumber(contentWidth)) {
                    childRect.width = contentWidth
                }
                if (isFiniteNumber(contentHeight)) {
                    childRect.height = contentHeight
                }

                switch (alignment) {
                    case "center":
                        if (isHorizontalDirection) {
                            childRect.y = scrollContainerSize.height / 2 - childRect.height / 2
                        } else {
                            childRect.x = scrollContainerSize.width / 2 - childRect.width / 2
                        }
                        break
                    case "end":
                        if (isHorizontalDirection) {
                            childRect.y = scrollContainerSize.height - childRect.height
                        } else {
                            childRect.x = scrollContainerSize.width - childRect.width
                        }
                        break
                    case ContentDimension.Stretch:
                        if (isHorizontalDirection) {
                            childRect.height = scrollContainerSize.height
                        } else {
                            childRect.width = scrollContainerSize.width
                        }
                }

                const isLastPage = index === pageCount - 1
                const gapOffset = isLastPage ? 0 : gap

                if (isHorizontalDirection) {
                    maxX += childRect.width + gapOffset
                } else {
                    maxY += childRect.height + gapOffset
                }
                const update = {
                    width: childRect.width,
                    height: childRect.height,
                    right: undefined,
                    bottom: undefined,
                    top: 0,
                    left: 0,
                    _constraints: {
                        enabled: true,
                    },
                }

                let effect: { [key: string]: MotionValue } | undefined
                const values = this.effectValues(index, childRect)

                if (values) {
                    // We use an ObservableObject here to make all values Animatable
                    // So we can set them in the onMove function
                    effect = {}
                    for (const key in values) {
                        effect[key] = motionValue(values[key])
                    }
                }
                this.pages.push({
                    rect: childRect,
                    effectValues: effect,
                })

                return (
                    <PageContainer key={index} effect={effect} {...childRect}>
                        {React.cloneElement(child, update)}
                    </PageContainer>
                )
            }
        )

        const maxScrollOffset = Math.max(
            0,
            isHorizontalDirection ? maxX - scrollContainerSize.width : maxY - scrollContainerSize.height
        )
        this.pages.forEach(page => {
            if (isHorizontalDirection) {
                if (page.rect.x > maxScrollOffset) {
                    page.rect.x = maxScrollOffset
                }
            } else {
                if (page.rect.y > maxScrollOffset) {
                    page.rect.y = maxScrollOffset
                }
            }
        })
        if (!this.contentOffset) {
            this.contentOffset = {
                x: isMotionValue(contentOffsetX) ? contentOffsetX : motionValue(contentOffsetX || 0),
                y: isMotionValue(contentOffsetY) ? contentOffsetY : motionValue(contentOffsetY || 0),
            }

            if (RenderEnvironment.target !== RenderTarget.canvas) {
                this.contentOffset.x.onChange(this.contentOffsetUpdated)
                this.contentOffset.y.onChange(this.contentOffsetUpdated)
            }
            const boundedCurrentPage = this.boundedCurrentPage(this.props as PageProps)
            this.updateOffsetForPage(boundedCurrentPage)
        }

        // Reset the content offset orthogonal to the scroll direction
        const crossScrollDirection = isHorizontalDirection ? this.contentOffset.y : this.contentOffset.x
        crossScrollDirection.set(0)

        const calculatedContentWidth = isHorizontalDirection ? maxX : scrollContainerSize.width
        const calculatedContentHeight = isHorizontalDirection ? scrollContainerSize.height : maxY
        const numChildren = React.Children.count(childComponents)
        const currentContentWidth = numChildren === 0 ? defaultFrameRect.width : calculatedContentWidth
        const currentContentHeight = numChildren === 0 ? defaultFrameRect.height : calculatedContentHeight
        return (
            <FrameWithMotion
                preserve3d={false}
                perspective={1200}
                overflow={overflow}
                {...props}
                ref={shouldMeasureSize ? this.containerRef : null}
            >
                <Scroll
                    background={null}
                    direction={direction}
                    directionLock={directionLock}
                    dragEnabled={dragEnabled}
                    wheelEnabled={wheelEnabled}
                    width={scrollContainerSize.width}
                    height={scrollContainerSize.height}
                    left={padding.left}
                    top={padding.top}
                    preserve3d={true}
                    contentOffsetX={this.contentOffset.x}
                    contentOffsetY={this.contentOffset.y}
                    scrollAnimate={this.scrollControls}
                    onDrag={onDrag}
                    onDragStart={this.wrapHandler(this.onDragStart, onDragStart)}
                    onDragEnd={this.wrapHandler(this.onDragEnd, onDragEnd)}
                    onScroll={onScroll}
                    onScrollStart={onScrollStart}
                    onScrollEnd={onScrollEnd}
                    onDirectionLock={this.wrapHandler(this.onDirectionLock, onDirectionLock)}
                    onUpdate={onUpdate}
                    contentWidth={currentContentWidth}
                    contentHeight={currentContentHeight}
                    overflow={"visible"}
                >
                    <FrameWithMotion
                        background={null}
                        top={0}
                        left={0}
                        width={currentContentWidth}
                        height={currentContentHeight}
                        preserve3d={true}
                    >
                        <EmptyState
                            children={children}
                            size={{
                                width: size.width || unwrapMotionValue(props.width),
                                height: size.height || unwrapMotionValue(props.height),
                            }}
                            insideUserCodeComponent={!this.props.__fromCodeComponentNode}
                        />
                        {childComponents}
                    </FrameWithMotion>
                </Scroll>
            </FrameWithMotion>
        )
    }
}

// Effects

function cubeEffect(info: PageEffectInfo) {
    const { normalizedOffset, direction } = info
    const isHorizontal = direction === "horizontal"

    return {
        originX: normalizedOffset < 0 ? 1 : 0,
        originY: normalizedOffset < 0 ? 1 : 0,
        rotateY: isHorizontal ? Math.min(Math.max(-90, normalizedOffset * 90), 90) : 0,
        rotateX: isHorizontal ? 0 : Math.min(Math.max(-90, normalizedOffset * -90), 90),
        backfaceVisibility: "hidden",
        WebkitBackfaceVisibility: "hidden",
    }
}

function coverflowEffect(info: PageEffectInfo) {
    const { normalizedOffset, direction, size } = info
    const isHorizontal = direction === "horizontal"

    return {
        rotateY: isHorizontal ? Math.min(45, Math.max(-45, normalizedOffset * -45)) : 0,
        rotateX: isHorizontal ? 0 : Math.min(45, Math.max(-45, normalizedOffset * 45)),
        originX: isHorizontal ? (normalizedOffset < 0 ? 0 : 1) : 0.5,
        originY: isHorizontal ? 0.5 : normalizedOffset < 0 ? 0 : 1,
        x: isHorizontal ? (normalizedOffset * -size.width) / 4 : 0,
        y: isHorizontal ? 0 : (normalizedOffset * -size.height) / 4,
        z: -Math.abs(normalizedOffset),
        scale: 1 - Math.abs(normalizedOffset / 10),
    }
}

function pileEffect(info: PageEffectInfo) {
    const { normalizedOffset, direction, size } = info
    const isHorizontal = direction === "horizontal"
    return {
        x: normalizedOffset < 0 && isHorizontal ? Math.abs(normalizedOffset) * (size.width - 8) : 0,
        y: normalizedOffset < 0 && !isHorizontal ? Math.abs(normalizedOffset) * (size.height - 8) : 0,
        scale: normalizedOffset < 0 ? 1 - Math.abs(normalizedOffset) / 50 : 1,
    }
}

function wheelEffect(info: PageEffectInfo) {
    const { normalizedOffset, direction, size } = info
    const isHorizontal = direction === "horizontal"

    const originZ = ((isHorizontal ? size.width : size.height) * 18) / (2 * Math.PI)
    const rotateX = isHorizontal ? 0 : normalizedOffset * -20
    const rotateY = isHorizontal ? normalizedOffset * 20 : 0
    const y = isHorizontal ? 0 : normalizedOffset * -size.height
    const x = isHorizontal ? normalizedOffset * -size.width : 0

    return {
        opacity: 1 - Math.abs(normalizedOffset) / 4,
        transform: `translate(${x}px, ${y}px) translateZ(-${originZ}px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateZ(${originZ}px)`,
    }
}

function defaultEffects(type: PageEffect): CustomPageEffect | null {
    switch (type) {
        case "cube":
            return cubeEffect
        case "coverflow":
            return coverflowEffect
        case "pile":
            return pileEffect
        case "wheel":
            return wheelEffect
        default:
            return null
    }
}
